import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import {
  AlertTriangle, Building2, Calendar, CheckCircle, Clock,
  Download, Edit2, Eye, Filter, Plus, Search, Trash2,
  TrendingUp, X, XCircle, BarChart2, Users, Zap, Activity, FileText,
  FileSpreadsheet, ChevronLeft, ChevronRight, Paperclip, ImagePlus,
  Image as ImageIcon, Mail, Phone, Network,
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import * as XLSX from 'xlsx';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Area,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL;

const TIPOS = [
  'Soporte Técnico', 'Error de Sistema', 'Solicitud de Cambio', 'Consulta', 'Formación',
  'Configuración', 'Integración', 'Facturación', 'Acceso/Permisos', 'Rendimiento', 'Otros',
];
const PRIORIDADES = [
  { value: 'baja',    label: 'Baja',    color: 'bg-gray-100 text-gray-600' },
  { value: 'media',   label: 'Media',   color: 'bg-sky-100 text-sky-700' },
  { value: 'alta',    label: 'Alta',    color: 'bg-amber-100 text-amber-700' },
  { value: 'critica', label: 'Crítica', color: 'bg-rose-100 text-rose-700' },
];
const ESTADOS = [
  { value: 'abierta',    label: 'Abierta',    dot: 'bg-sky-500',     selectedCls: 'border-sky-300 bg-sky-50 text-sky-700' },
  { value: 'en_proceso', label: 'En proceso', dot: 'bg-amber-500',   selectedCls: 'border-amber-300 bg-amber-50 text-amber-700' },
  { value: 'resuelta',   label: 'Resuelta',   dot: 'bg-emerald-500', selectedCls: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
];

const PIE_COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#f43f5e','#8b5cf6','#14b8a6','#f97316'];
const PRIO_COLORS = { critica:'#f43f5e', alta:'#f59e0b', media:'#0ea5e9', baja:'#9ca3af' };
const ESTADO_COLORS = { abierta:'#0ea5e9', en_proceso:'#f59e0b', resuelta:'#10b981', cerrada:'#9ca3af' }; // cerrada se mantiene para datos históricos

function getPrioridadBadge(p) {
  return { baja:'bg-gray-100 text-gray-600', media:'bg-sky-100 text-sky-700',
           alta:'bg-amber-100 text-amber-700', critica:'bg-rose-100 text-rose-700' }[p] || 'bg-gray-100 text-gray-600';
}
function getEstadoBadge(e) {
  return {
    abierta:    { dot:'bg-sky-500',     label:'Abierta',    badge:'bg-sky-50 text-sky-700 border border-sky-200' },
    en_proceso: { dot:'bg-amber-500',   label:'En proceso', badge:'bg-amber-50 text-amber-700 border border-amber-200' },
    resuelta:   { dot:'bg-emerald-500', label:'Resuelta',   badge:'bg-emerald-50 text-emerald-700 border border-emerald-200' },
    cerrada:    { dot:'bg-gray-400',    label:'Cerrada',    badge:'bg-gray-100 text-gray-500 border border-gray-200' },
  }[e] || { dot:'bg-gray-400', label: e, badge:'bg-gray-100 text-gray-500 border border-gray-200' };
}

/* ── SEDE ────────────────────────────────────────────────────────────────────
   No existe tabla `sucursales`: las sedes son filas de `clientes` cuyo campo
   clinica_matriz apunta a la matriz. clinica_matriz = 0 (o NULL) => ES la matriz.
   El backend devuelve clinica_matriz + matriz_nombre/matriz_alias (LEFT JOIN).   */
const esSede       = (inc) => !!(inc?.clinica_matriz && Number(inc.clinica_matriz) !== 0);
const nombreMatriz = (inc) => inc?.matriz_alias || inc?.matriz_nombre || '—';

/* Colores del badge de sede en HEX: Tailwind v2.2.19 estático no compila
   utilidades fuera de su paleta base, así que los críticos van inline. */
const SEDE_BADGE   = { backgroundColor: '#dbeafe', color: '#1d4ed8' }; // blue
const MATRIZ_BADGE = { backgroundColor: '#e0e7ff', color: '#4338ca' }; // indigo

/* Los adjuntos se guardan con url relativa '/api/archivos/:id' (la sirve
   routes/Archivos.js SIN auth para que funcione <img src>). Si el API vive en
   otro origen hay que anteponerlo; si ya es absoluta, se deja tal cual. */
const archivoSrc = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

const inputCls =
  'w-full px-3 h-9 border border-gray-200 rounded-lg text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent ' +
  'bg-white transition-shadow hover:border-gray-300';
const searchInputCls =
  'w-full pl-8 pr-3 h-9 border border-gray-200 rounded-lg text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent ' +
  'bg-white transition-shadow hover:border-gray-300';
const labelCls = 'block text-xs font-medium text-gray-500 mb-1';

/* ── Custom Tooltip ──────────────────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-3 py-2.5 text-xs">
      <p className="font-semibold text-gray-600 mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span className="text-gray-400">{p.name}:</span>
          <span className="font-semibold text-gray-800">
            {typeof p.value === 'number' ? p.value.toLocaleString('es-ES', { maximumFractionDigits: 1 }) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ── Chart Type Toggle ───────────────────────────────────────────────────── */
function ChartToggle({ options, value, onChange }) {
  return (
    <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)}
          title={opt.label}
          className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
            value === opt.value
              ? 'bg-white shadow-sm text-gray-700'
              : 'text-gray-400 hover:text-gray-600'
          }`}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/* ── Form ────────────────────────────────────────────────────────────────── */
function IncidenciaForm({
  data, onChange, clienteSearch, onClienteSearch, clienteResults, onClienteSelect, onClienteRemove,
  // Adjuntos (opcionales: si no se pasan, el form se comporta como antes)
  archivos = [],          // File[] pendientes de subir
  onArchivosAdd,          // (FileList) => void
  onArchivoRemove,        // (index) => void
  adjuntos = [],          // adjuntos YA guardados en servidor (modo edición)
  onAdjuntoRemove,        // (id) => void
}) {
  const fileRef = useRef(null);

  // Los blob: URLs de la preview se generan una sola vez por selección y se
  // revocan al cambiar/desmontar. Sin esto, cada render filtra memoria.
  const previews = useMemo(
    () => archivos.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [archivos],
  );
  useEffect(
    () => () => previews.forEach((p) => URL.revokeObjectURL(p.url)),
    [previews],
  );

  const hayAdjuntos = adjuntos.length > 0 || previews.length > 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="md:col-span-2">
        <label className={labelCls}>Nombre <span className="text-rose-500">*</span></label>
        <input type="text" value={data.nombre} onChange={e => onChange('nombre', e.target.value)}
          placeholder="Título de la incidencia..." className={inputCls} />
      </div>

      <div className="md:col-span-2">
        <label className={labelCls}>Cliente <span className="text-rose-500">*</span></label>
        {data.cliente_id ? (
          <div className="flex items-center h-9 bg-emerald-50 border border-emerald-200 rounded-lg px-3 gap-2">
            <Building2 size={13} className="text-emerald-500 flex-shrink-0" />
            <span className="flex-1 text-sm font-medium text-emerald-800 truncate">{data.cliente_nombre}</span>
            <button onClick={onClienteRemove} className="text-emerald-400 hover:text-rose-500 flex-shrink-0"><X size={13} /></button>
          </div>
        ) : (
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={clienteSearch} onChange={e => onClienteSearch(e.target.value)}
              placeholder="Buscar cliente..." className={searchInputCls} />
            {clienteResults.length > 0 && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                {clienteResults.map(c => (
                  <button key={c.id} onClick={() => onClienteSelect(c)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-0">
                    <div className="font-medium text-gray-800">{c.razon_social}</div>
                    {c.alias && <div className="text-xs text-gray-400">{c.alias}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <label className={labelCls}>Fecha <span className="text-rose-500">*</span></label>
        <input type="date" value={data.fecha} onChange={e => onChange('fecha', e.target.value)} className={inputCls} />
      </div>

      <div>
        <label className={labelCls}>Carga de trabajo (h) <span className="text-rose-500">*</span></label>
        <input type="number" min="0" step="0.25" value={data.carga_trabajo}
          onChange={e => onChange('carga_trabajo', e.target.value)} className={inputCls} />
      </div>

      <div>
        <label className={labelCls}>Tipo</label>
        <select value={data.tipo_incidencia} onChange={e => onChange('tipo_incidencia', e.target.value)} className={inputCls}>
          <option value="">Sin tipo</option>
          {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div>
        <label className={labelCls}>Fecha resolución</label>
        <input type="date" value={data.fecha_resolucion} onChange={e => onChange('fecha_resolucion', e.target.value)} className={inputCls} />
      </div>

      <div className="md:col-span-2">
        <label className={labelCls}>Prioridad</label>
        <div className="flex gap-1.5 h-9">
          {PRIORIDADES.map(p => (
            <button key={p.value} type="button" onClick={() => onChange('prioridad', p.value)}
              className={`flex-1 rounded-lg text-xs font-semibold border-2 transition-all ${
                data.prioridad === p.value ? `${p.color} border-current shadow-sm` : 'border-transparent bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}>{p.label}</button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className={labelCls}>Estado</label>
        <div className="flex gap-1.5 h-9">
          {ESTADOS.map(e => (
            <button key={e.value} type="button" onClick={() => onChange('estado', e.value)}
              className={`flex-1 rounded-lg text-xs font-semibold border-2 transition-all flex items-center justify-center gap-1 ${
                data.estado === e.value ? 'border-gray-300 bg-white text-gray-800 shadow-sm' : 'border-transparent bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${e.dot}`} />
              <span className="truncate">{e.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        <label className={labelCls}>Descripción</label>
        <textarea value={data.descripcion} onChange={e => onChange('descripcion', e.target.value)}
          rows={3} placeholder="Describe la incidencia..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none hover:border-gray-300" />
      </div>

      <div className="md:col-span-2">
        <label className={labelCls}>Notas de resolución</label>
        <textarea value={data.resolucion} onChange={e => onChange('resolucion', e.target.value)}
          rows={3} placeholder="Solución aplicada..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none hover:border-gray-300" />
      </div>

      {/* ── Imágenes adjuntas ─────────────────────────────────────────────── */}
      {onArchivosAdd && (
        <div className="md:col-span-4">
          <label className={labelCls}>Imágenes adjuntas</label>

          {/* input real oculto: el botón visible lo dispara (patrón BlogAdmin) */}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files?.length) onArchivosAdd(e.target.files);
              e.target.value = ''; // permite re-seleccionar el mismo fichero
            }}
          />

          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => fileRef.current?.click()}
              className="px-3 h-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 flex items-center gap-1.5 transition-colors">
              <ImagePlus size={14} /> Añadir imágenes
            </button>
            <span className="text-xs text-gray-400">
              JPG, PNG, GIF o WEBP · máx. 15MB por imagen
            </span>
          </div>

          {hayAdjuntos && (
            <div className="flex flex-wrap gap-2 mt-3">
              {/* Ya guardadas en servidor: quitarlas marca su id para borrado */}
              {adjuntos.map((a) => (
                <div key={`srv-${a.id}`} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <img src={archivoSrc(a.url)} alt={a.nombre_original}
                    style={{ width: '88px', height: '88px', objectFit: 'cover', display: 'block' }} />
                  {onAdjuntoRemove && (
                    <button type="button" onClick={() => onAdjuntoRemove(a.id)}
                      title="Quitar imagen"
                      className="absolute top-1 right-1 rounded-full text-white flex items-center justify-center hover:opacity-100"
                      style={{ width: '18px', height: '18px', backgroundColor: 'rgba(220,38,38,.9)', opacity: .92 }}>
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}

              {/* Pendientes de subir */}
              {previews.map((p, idx) => (
                <div key={`new-${idx}`} className="relative rounded-lg overflow-hidden border-2 border-green-300 bg-gray-50">
                  <img src={p.url} alt={p.file.name}
                    style={{ width: '88px', height: '88px', objectFit: 'cover', display: 'block' }} />
                  <button type="button" onClick={() => onArchivoRemove(idx)}
                    title="Quitar imagen"
                    className="absolute top-1 right-1 rounded-full text-white flex items-center justify-center"
                    style={{ width: '18px', height: '18px', backgroundColor: 'rgba(220,38,38,.9)' }}>
                    <X size={11} />
                  </button>
                  <span className="absolute bottom-0 left-0 right-0 text-white text-center truncate px-1"
                    style={{ fontSize: '9px', backgroundColor: 'rgba(0,0,0,.55)' }}>
                    nueva
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Stat KPI Card ───────────────────────────────────────────────────────── */
function StatCard({ label, value, sub, icon, colorText, colorBg, border }) {
  return (
    <div className={`bg-white rounded-xl border ${border} px-4 py-3 shadow-sm`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg ${colorBg} ${colorText}`}>{icon}</div>
        <span className="text-xs font-medium text-gray-500 leading-tight">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900 tracking-tight">{value}</div>
      <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────────────────────── */
export default function Incidencias() {
  const { user }  = useContext(AuthContext);
  const { theme } = useTheme();

  const bgPrimary      = { backgroundColor: theme.primary };
  const bgPrimaryLight = { backgroundColor: theme.primaryLight };
  const textPrimary    = { color: theme.primary };

  const [vistaActiva,   setVistaActiva]   = useState('lista');
  const [incidencias,   setIncidencias]   = useState([]);
  const [estadisticas,  setEstadisticas]  = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [mensaje,       setMensaje]       = useState(null);
  const [page,          setPage]          = useState(1);
  const [totalPages,    setTotalPages]    = useState(1);

  // Chart type toggles per chart
  const [ct, setCt] = useState({
    evolucion:  'composed',
    topInc:     'bar',
    topHoras:   'bar',
    prioridad:  'bar',
    estado:     'pie',
    tipo:       'pie',
    horasTipo:  'bar',
  });
  const setChartType = (key, val) => setCt(p => ({ ...p, [key]: val }));

  const [filtros, setFiltros] = useState({
    nombre:'', cliente_id:'', estado:'', prioridad:'', tipo:'', fecha_desde:'', fecha_hasta:'',
  });
  const emptyForm = {
    nombre:'', cliente_id:'', cliente_nombre:'',
    fecha: new Date().toISOString().slice(0,10),
    carga_trabajo:0, tipo_incidencia:'', prioridad:'media', estado:'abierta',
    descripcion:'', fecha_resolucion:'', resolucion:'',
  };
  const [formData,           setFormData]           = useState(emptyForm);
  const [clienteSearch,      setClienteSearch]      = useState('');
  const [clienteResults,     setClienteResults]     = useState([]);
  const [editForm,           setEditForm]           = useState(emptyForm);
  const [editClienteSearch,  setEditClienteSearch]  = useState('');
  const [editClienteResults, setEditClienteResults] = useState([]);
  const [modalDetalle,       setModalDetalle]       = useState(null);
  const [modalEditar,        setModalEditar]        = useState(null);
  const [modalEliminar,      setModalEliminar]      = useState(null);

  // ── Adjuntos ────────────────────────────────────────────────────────────────
  const [formArchivos,        setFormArchivos]        = useState([]); // File[] al crear
  const [editArchivos,        setEditArchivos]        = useState([]); // File[] nuevos al editar
  const [editAdjuntos,        setEditAdjuntos]        = useState([]); // ya en servidor
  const [editAdjuntosBorrar,  setEditAdjuntosBorrar]  = useState([]); // ids a eliminar

  // ── Detalle enriquecido + lightbox ──────────────────────────────────────────
  //   El modal se abre al instante con el objeto del listado y se enriquece con
  //   GET /api/incidencias/:id (trae teléfono, email y adjuntos).
  const [detalle,        setDetalle]        = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [lightboxOpen,   setLightboxOpen]   = useState(false);
  const [lightboxIndex,  setLightboxIndex]  = useState(0);

  // ── Tablero kanban ──────────────────────────────────────────────────────────
  const [listaVista, setListaVista] = useState('tablero'); // 'tabla' | 'tablero'
  const [dragId,     setDragId]     = useState(null);
  const [dropCol,    setDropCol]    = useState(null);

  // ── Reporte mensual ─────────────────────────────────────────────────────────
  const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const primerDiaMes = () => { const d = new Date(); return fmtLocal(new Date(d.getFullYear(), d.getMonth(), 1)); };
  const ultimoDiaMes = () => { const d = new Date(); return fmtLocal(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };
  const [repFiltros,        setRepFiltros]        = useState({ desde: primerDiaMes(), hasta: ultimoDiaMes() });
  const [repClinicas,       setRepClinicas]       = useState([]); // [{id, razon_social}]
  const [repClienteSearch,  setRepClienteSearch]  = useState('');
  const [repClienteResults, setRepClienteResults] = useState([]);
  const [repDatos,          setRepDatos]          = useState(null);
  const [repLoading,        setRepLoading]        = useState(false);
  const [repEnviando,       setRepEnviando]       = useState(false);
  const [repEmail,          setRepEmail]          = useState('');
  const [repFormato,        setRepFormato]        = useState('ambos');

  const searchTimer     = useRef(null);
  const editSearchTimer = useRef(null);
  const repSearchTimer  = useRef(null);
  const tkn = () => localStorage.getItem('token');

  const showMsg = (text, type = 'success') => {
    setMensaje({ text, type });
    setTimeout(() => setMensaje(null), 4000);
  };

  const fetchIncidencias = useCallback(async (pageNum = 1, overrideFiltros = null) => {
    try {
      setLoading(true);
      const f = overrideFiltros || filtros;
      const params = new URLSearchParams({ page: pageNum, limit: 200 });
      Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${API_URL}/api/incidencias?${params}`,
        { headers: { Authorization: `Bearer ${tkn()}` } });
      const data = await res.json();
      setIncidencias(data.incidencias || []);
      setTotalPages(Math.ceil((data.total || 0) / 200));
    } catch { showMsg('Error al cargar incidencias', 'error'); }
    finally { setLoading(false); }
  }, [filtros]);

  const fetchEstadisticas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/incidencias/estadisticas`,
        { headers: { Authorization: `Bearer ${tkn()}` } });
      const data = await res.json();
      setEstadisticas(data);
    } catch { showMsg('Error al cargar estadísticas', 'error'); }
  };

  useEffect(() => { fetchIncidencias(1); }, []);
  useEffect(() => { if (vistaActiva === 'estadisticas') fetchEstadisticas(); }, [vistaActiva]);

  // ── Detalle: carga la ficha completa al abrir el modal ──────────────────────
  //   El listado no trae cliente_telefono / cliente_email / adjuntos; GET /:id sí.
  //   `cancelado` evita el setState sobre un modal ya cerrado (React warning).
  useEffect(() => {
    if (!modalDetalle) { setDetalle(null); setLightboxOpen(false); return; }
    let cancelado = false;
    (async () => {
      setDetalleLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/incidencias/${modalDetalle.id}`,
          { headers: { Authorization: `Bearer ${tkn()}` } });
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!cancelado) setDetalle(data.incidencia || null);
      } catch {
        // El modal sigue siendo usable con los datos del listado.
        if (!cancelado) showMsg('No se pudo cargar el detalle completo', 'error');
      } finally {
        if (!cancelado) setDetalleLoading(false);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalDetalle?.id]);

  // Fuente de verdad del modal: la ficha completa si ya llegó, si no lo que
  // tenemos del listado (así el modal abre instantáneo y se enriquece solo).
  const D = detalle || modalDetalle || {};

  // Imágenes del detalle (los adjuntos no-imagen, si algún día los hubiera, se ignoran aquí)
  const detalleImgs = (detalle?.adjuntos || []).filter(a => a.es_imagen);

  // ── Lightbox — misma lógica que BlogPost.js, markup en Tailwind ─────────────
  const openLightbox  = (index) => { setLightboxIndex(index); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);
  const navigateLightbox = (dir) => {
    setLightboxIndex(prev => {
      const next = prev + dir;
      if (next < 0) return detalleImgs.length - 1;
      if (next >= detalleImgs.length) return 0;
      return next;
    });
  };

  // Navegación por teclado del lightbox
  useEffect(() => {
    const handleKey = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape')     closeLightbox();
      if (e.key === 'ArrowLeft')  navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, detalleImgs.length]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (vistaActiva === 'reporte') fetchReporteDatos(); }, [vistaActiva]);

  // ── Reporte: búsqueda y selección de clínicas (multi) ───────────────────────
  const buscarClientesRep = (val) => {
    setRepClienteSearch(val);
    clearTimeout(repSearchTimer.current);
    repSearchTimer.current = setTimeout(() => buscarClientes(val, setRepClienteResults), 300);
  };
  const addClinicaRep = (c) => {
    setRepClinicas(prev => prev.some(x => x.id === c.id)
      ? prev
      : [...prev, { id: c.id, razon_social: c.razon_social || c.alias || `#${c.id}` }]);
    setRepClienteSearch(''); setRepClienteResults([]);
  };
  const removeClinicaRep = (id) => setRepClinicas(prev => prev.filter(x => x.id !== id));

  const repParams = () => {
    const p = new URLSearchParams();
    if (repFiltros.desde) p.append('desde', repFiltros.desde);
    if (repFiltros.hasta) p.append('hasta', repFiltros.hasta);
    if (repClinicas.length) p.append('clientes', repClinicas.map(c => c.id).join(','));
    return p;
  };

  const fetchReporteDatos = async () => {
    try {
      setRepLoading(true);
      const res = await fetch(`${API_URL}/api/incidencias/reporte/datos?${repParams()}`,
        { headers: { Authorization: `Bearer ${tkn()}` } });
      if (!res.ok) throw new Error();
      setRepDatos(await res.json());
    } catch { showMsg('Error al calcular el reporte', 'error'); }
    finally { setRepLoading(false); }
  };

  const descargarReporte = async (formato) => {
    try {
      const p = repParams(); p.append('formato', formato);
      const res = await fetch(`${API_URL}/api/incidencias/reporte?${p}`,
        { headers: { Authorization: `Bearer ${tkn()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `reporte_incidencias_${repFiltros.desde}_${repFiltros.hasta}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { showMsg('Error al descargar el reporte', 'error'); }
  };

  const enviarReporteEmail = async () => {
    try {
      setRepEnviando(true);
      const res = await fetch(`${API_URL}/api/incidencias/reporte/enviar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          desde: repFiltros.desde, hasta: repFiltros.hasta,
          clientes: repClinicas.map(c => c.id), formato: repFormato,
          email: repEmail.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showMsg(data.message || 'Reporte enviado');
    } catch (e) { showMsg(e.message || 'Error al enviar el reporte', 'error'); }
    finally { setRepEnviando(false); }
  };

  const buscarClientes = async (val, setResults) => {
    if (val.length < 2) { setResults([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/clientes/search?query=${encodeURIComponent(val)}`,
        { headers: { Authorization: `Bearer ${tkn()}` } });
      const data = await res.json();
      setResults(data.clientes || []);
    } catch { setResults([]); }
  };

  const handleClienteSearch = (val) => {
    setClienteSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => buscarClientes(val, setClienteResults), 300);
  };
  const handleEditClienteSearch = (val) => {
    setEditClienteSearch(val);
    clearTimeout(editSearchTimer.current);
    editSearchTimer.current = setTimeout(() => buscarClientes(val, setEditClienteResults), 300);
  };

  // Construye el body de creación/edición.
  //   Con archivos -> multipart/form-data. CLAVE: NO se toca Content-Type, el
  //   browser tiene que poner el boundary él solo (si lo forzamos, multer no
  //   parsea nada y llegan 0 ficheros).
  //   Sin archivos -> JSON de toda la vida, para no cambiar el camino habitual.
  const construirPeticion = (payload, files, idsBorrar) => {
    const headers = { Authorization: `Bearer ${tkn()}` };
    if (files.length) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => fd.append(k, v ?? ''));
      if (idsBorrar?.length) fd.append('archivos_eliminar', JSON.stringify(idsBorrar));
      files.forEach(f => fd.append('archivos', f));
      return { headers, body: fd };
    }
    return {
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(idsBorrar?.length ? { ...payload, archivos_eliminar: idsBorrar } : payload),
    };
  };

  const handleCrear = async () => {
    if (!formData.nombre || !formData.cliente_id || !formData.fecha)
      return showMsg('Nombre, cliente y fecha son obligatorios', 'error');
    try {
      const { headers, body } = construirPeticion(formData, formArchivos);
      const res = await fetch(`${API_URL}/api/incidencias`, { method:'POST', headers, body });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message);
      }
      showMsg('Incidencia creada correctamente');
      setFormData(emptyForm); setClienteSearch(''); setClienteResults([]);
      setFormArchivos([]);
      setVistaActiva('lista'); fetchIncidencias(1);
    } catch (e) { showMsg(e.message || 'Error al crear la incidencia', 'error'); }
  };

  const handleEditar = async () => {
    if (!editForm.nombre || !editForm.cliente_id || !editForm.fecha)
      return showMsg('Nombre, cliente y fecha son obligatorios', 'error');
    try {
      const { headers, body } = construirPeticion(editForm, editArchivos, editAdjuntosBorrar);
      const res = await fetch(`${API_URL}/api/incidencias/${modalEditar.id}`,
        { method:'PUT', headers, body });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message);
      }
      showMsg('Incidencia actualizada');
      setModalEditar(null);
      setEditArchivos([]); setEditAdjuntos([]); setEditAdjuntosBorrar([]);
      fetchIncidencias(page);
    } catch (e) { showMsg(e.message || 'Error al actualizar la incidencia', 'error'); }
  };

  const handleEliminar = async () => {
    try {
      const res = await fetch(`${API_URL}/api/incidencias/${modalEliminar.id}`,
        { method:'DELETE', headers: { Authorization:`Bearer ${tkn()}` } });
      if (!res.ok) throw new Error();
      showMsg('Incidencia eliminada'); setModalEliminar(null); fetchIncidencias(page);
    } catch { showMsg('Error al eliminar la incidencia', 'error'); }
  };

  const abrirEditar = async (inc) => {
    setEditForm({
      nombre: inc.nombre||'', cliente_id: inc.cliente_id||'', cliente_nombre: inc.cliente_nombre||'',
      fecha: inc.fecha ? inc.fecha.slice(0,10) : '', carga_trabajo: inc.carga_trabajo||0,
      tipo_incidencia: inc.tipo_incidencia||'', prioridad: inc.prioridad||'media',
      estado: inc.estado||'abierta', descripcion: inc.descripcion||'',
      fecha_resolucion: inc.fecha_resolucion ? inc.fecha_resolucion.slice(0,10) : '',
      resolucion: inc.resolucion||'',
    });
    setEditClienteSearch(''); setEditClienteResults([]);
    // Adjuntos: se pintan al vuelo si ya venían con el objeto (p.ej. desde el
    // detalle ya cargado) y se refrescan contra el servidor por si acaso.
    setEditArchivos([]); setEditAdjuntosBorrar([]);
    setEditAdjuntos(inc.adjuntos || []);
    setModalEditar(inc);
    try {
      const res = await fetch(`${API_URL}/api/incidencias/${inc.id}`,
        { headers: { Authorization: `Bearer ${tkn()}` } });
      if (!res.ok) return;                      // el form es usable igual
      const data = await res.json();
      setEditAdjuntos(data.incidencia?.adjuntos || []);
    } catch { /* silencioso: editar no depende de los adjuntos */ }
  };

  // Añade ficheros evitando duplicados obvios (mismo nombre + tamaño), que es lo
  // que pasa cuando el usuario vuelve a abrir el selector y re-elige lo mismo.
  const anadirArchivos = (setter) => (fileList) => {
    const nuevos = Array.from(fileList);
    setter(prev => {
      const clave = (f) => `${f.name}|${f.size}`;
      const yaEstan = new Set(prev.map(clave));
      return [...prev, ...nuevos.filter(f => !yaEstan.has(clave(f)))];
    });
  };
  const quitarArchivo = (setter) => (idx) => setter(prev => prev.filter((_, i) => i !== idx));

  // Quitar un adjunto YA guardado: sale de la vista y su id viaja en
  // archivos_eliminar al guardar (no se borra nada hasta confirmar el PUT).
  const quitarAdjuntoServidor = (id) => {
    setEditAdjuntos(prev => prev.filter(a => a.id !== id));
    setEditAdjuntosBorrar(prev => (prev.includes(id) ? prev : [...prev, id]));
  };

  // ── Kanban: columnas por estado + cambio de estado con update optimista ──────
  const KANBAN_COLS = [
    { key: 'abierta',    label: 'Abierta',    color: '#0ea5e9' },
    { key: 'en_proceso', label: 'En proceso', color: '#f59e0b' },
    { key: 'resuelta',   label: 'Resuelta',   color: '#10b981' },
    { key: 'cerrada',    label: 'Cerrada',    color: '#9ca3af' },
  ];

  const cambiarEstadoInc = async (inc, nuevoEstado) => {
    if (!inc || inc.estado === nuevoEstado) return;
    const anterior = inc.estado;
    const fechaResAnterior = inc.fecha_resolucion;
    const cierra = nuevoEstado === 'resuelta' || nuevoEstado === 'cerrada';
    // Update optimista (fluido, tipo JIRA)
    setIncidencias(prev => prev.map(x => x.id === inc.id
      ? { ...x, estado: nuevoEstado,
          fecha_resolucion: cierra ? (x.fecha_resolucion || new Date().toISOString().slice(0, 10)) : null }
      : x));
    try {
      const res = await fetch(`${API_URL}/api/incidencias/${inc.id}/estado`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setIncidencias(prev => prev.map(x => x.id === inc.id ? { ...x, estado: anterior, fecha_resolucion: fechaResAnterior } : x));
      showMsg('No se pudo cambiar el estado', 'error');
    }
  };

  const exportarCSV = () => {
    const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`; // escapa comillas internas
    const headers = ['ID','Nombre','Cliente','Sede','Matriz','Fecha','Tipo','Prioridad','Estado','Horas'];
    const rows = incidencias.map(i => [
      i.id, q(i.nombre), q(i.cliente_nombre),
      q(esSede(i) ? 'Sede' : 'Matriz'),
      q(esSede(i) ? (i.matriz_nombre || nombreMatriz(i)) : ''),
      i.fecha ? new Date(i.fecha).toLocaleDateString('es-ES') : '',
      q(i.tipo_incidencia||''), i.prioridad, i.estado, i.carga_trabajo,
    ]);
    const csv = [headers,...rows].map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download = 'incidencias.csv'; a.click();
  };

  const exportarEstadisticasXLSX = () => {
    if (!estadisticas) return;
    const wb = XLSX.utils.book_new();
    const addSheet = (name, rows, cols) => {
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = cols;
      XLSX.utils.book_append_sheet(wb, ws, name);
    };
    addSheet('Resumen KPIs',[
      ['KPI','Valor'],
      ['Total Incidencias', estadisticas.kpis?.total_incidencias??0],
      ['Total Horas', +parseFloat(estadisticas.kpis?.total_horas??0).toFixed(2)],
      ['Abiertas', estadisticas.kpis?.abiertas??0],
      ['En Proceso', estadisticas.kpis?.en_proceso??0],
      ['Resueltas', estadisticas.kpis?.resueltas??0],
      ['Cerradas', estadisticas.kpis?.cerradas??0],
      ['Críticas', estadisticas.kpis?.criticas??0],
      ['Este Mes', estadisticas.kpis?.este_mes??0],
      ['Horas Este Mes', +parseFloat(estadisticas.kpis?.horas_mes??0).toFixed(2)],
      ['Días Medio Resolución', +parseFloat(estadisticas.kpis?.avg_resolucion_dias??0).toFixed(1)],
    ],[{wch:28},{wch:15}]);
    addSheet('Evolución Mensual',
      [['Mes','Total','Horas'],...(estadisticas.evolucion_mensual||[]).map(m=>[m.mes,m.total,+parseFloat(m.horas||0).toFixed(2)])],
      [{wch:12},{wch:10},{wch:10}]);
    addSheet('Top Clientes (Incidencias)',
      [['Cliente','Alias','Total','Horas','Pendientes'],
       ...(estadisticas.top_clientes||[]).map(c=>[c.razon_social,c.alias||'',c.total,+parseFloat(c.horas||0).toFixed(2),c.pendientes])],
      [{wch:35},{wch:20},{wch:10},{wch:10},{wch:12}]);
    addSheet('Top Clientes (Horas)',
      [['Cliente','Alias','Horas','Total'],
       ...(estadisticas.top_clientes_horas||[]).map(c=>[c.razon_social,c.alias||'',+parseFloat(c.horas||0).toFixed(2),c.total])],
      [{wch:35},{wch:20},{wch:12},{wch:10}]);
    addSheet('Por Tipo',
      [['Tipo','Total','Horas'],
       ...(estadisticas.distribucion_tipo||[]).map(t=>[t.tipo_incidencia||'Sin tipo',t.total,+parseFloat(t.horas||0).toFixed(2)])],
      [{wch:25},{wch:10},{wch:10}]);
    addSheet('Por Prioridad',
      [['Prioridad','Total','Horas'],
       ...(estadisticas.distribucion_prioridad||[]).map(p=>[p.prioridad||'N/A',p.total,+parseFloat(p.horas||0).toFixed(2)])],
      [{wch:15},{wch:10},{wch:10}]);
    addSheet('Por Estado',
      [['Estado','Total','Horas'],
       ...(estadisticas.distribucion_estado||[]).map(e=>[e.estado||'N/A',e.total,+parseFloat(e.horas||0).toFixed(2)])],
      [{wch:15},{wch:10},{wch:10}]);
    XLSX.writeFile(wb, `estadisticas_incidencias_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  /* ── Chart renderers ─────────────────────────────────────────────────────── */
  const renderEvolucion = () => {
    const data = estadisticas?.evolucion_mensual || [];
    if (!data.length) return <p className="text-xs text-gray-400 flex items-center justify-center h-full">Sin datos</p>;
    const common = { margin:{ top:4, right:10, left:-15, bottom:4 } };
    if (ct.evolucion === 'bar') return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} {...common}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="mes" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <YAxis yAxisId="l" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <YAxis yAxisId="r" orientation="right" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <Tooltip content={<CustomTooltip />} /><Legend wrapperStyle={{ fontSize:10 }} />
          <Bar yAxisId="l" dataKey="total" name="Incidencias" fill={theme.primary} radius={[3,3,0,0]} />
          <Bar yAxisId="r" dataKey="horas" name="Horas" fill="#f59e0b" radius={[3,3,0,0]} />
        </BarChart>
      </ResponsiveContainer>
    );
    if (ct.evolucion === 'line') return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} {...common}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="mes" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <YAxis yAxisId="l" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <YAxis yAxisId="r" orientation="right" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <Tooltip content={<CustomTooltip />} /><Legend wrapperStyle={{ fontSize:10 }} />
          <Line yAxisId="l" type="monotone" dataKey="total" name="Incidencias" stroke={theme.primary} strokeWidth={2} dot={{ r:3 }} />
          <Line yAxisId="r" type="monotone" dataKey="horas" name="Horas" stroke="#f59e0b" strokeWidth={2} dot={{ r:3 }} />
        </LineChart>
      </ResponsiveContainer>
    );
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} {...common}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="mes" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <YAxis yAxisId="l" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <YAxis yAxisId="r" orientation="right" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <Tooltip content={<CustomTooltip />} /><Legend wrapperStyle={{ fontSize:10 }} />
          <Area yAxisId="l" type="monotone" dataKey="total" name="Incidencias"
            fill={theme.primaryLight} stroke={theme.primary} strokeWidth={2} fillOpacity={0.5} />
          <Line yAxisId="r" type="monotone" dataKey="horas" name="Horas"
            stroke="#f59e0b" strokeWidth={2} dot={{ r:3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  const renderHBarOrLine = (data, dataKey, nameKey, color, typeKey) => {
    if (!data?.length) return <p className="text-xs text-gray-400 flex items-center justify-center h-full">Sin datos</p>;
    if (ct[typeKey] === 'line') return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top:4, right:10, left:-10, bottom:4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey={nameKey} tick={{ fontSize:9, fill:'#9ca3af' }} interval={0} />
          <YAxis tick={{ fontSize:10, fill:'#9ca3af' }} />
          <Tooltip content={<CustomTooltip />} />
          <Line type="monotone" dataKey={dataKey} name={dataKey} stroke={color} strokeWidth={2} dot={{ r:3 }} />
        </LineChart>
      </ResponsiveContainer>
    );
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top:0, right:10, left:0, bottom:0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
          <XAxis type="number" tick={{ fontSize:10, fill:'#9ca3af' }} />
          <YAxis dataKey={nameKey} type="category" width={115} tick={{ fontSize:10, fill:'#6b7280' }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey={dataKey} name={dataKey} fill={color} radius={[0,4,4,0]} barSize={12} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderPieOrBar = (data, countKey, nameKey, typeKey, colorFn) => {
    if (!data?.length) return <p className="text-xs text-gray-400 flex items-center justify-center h-full">Sin datos</p>;
    if (ct[typeKey] === 'bar') return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top:4, right:10, left:-15, bottom:20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey={nameKey} tick={{ fontSize:9, fill:'#9ca3af' }} angle={-20} textAnchor="end" interval={0} />
          <YAxis tick={{ fontSize:10, fill:'#9ca3af' }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey={countKey} name="Total" radius={[4,4,0,0]} barSize={28}>
            {data.map((d,i) => <Cell key={i} fill={colorFn(d,i)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey={countKey} nameKey={nameKey}
            cx="50%" cy="46%" outerRadius="68%" innerRadius="36%" paddingAngle={2}>
            {data.map((d,i) => <Cell key={i} fill={colorFn(d,i)} />)}
          </Pie>
          <Tooltip content={({ active, payload }) => {
            if (!active||!payload?.length) return null;
            const d = payload[0];
            return (
              <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-3 py-2 text-xs">
                <p className="font-semibold text-gray-700">{d.name||'—'}</p>
                <p className="text-gray-500">Total: <span className="font-bold text-gray-800">{d.value}</span></p>
              </div>
            );
          }} />
          <Legend formatter={v=><span className="text-[9px] text-gray-500">{v&&v.length>15?v.slice(0,15)+'…':(v||'—')}</span>} iconSize={7} />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  /* ── RENDER ─────────────────────────────────────────────────────────────── */
  return (
    // position:fixed ancla el componente al viewport ignorando cualquier scroll del padre
    <div style={{ position:'fixed', top:'56px', left:0, right:0, bottom:0, overflow:'hidden', display:'flex', flexDirection:'column', background:'#f9fafb', zIndex:1 }}>

      {/* Toast */}
      {mensaje && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-xl shadow-xl text-white text-sm flex items-center gap-2 font-medium ${
          mensaje.type==='error' ? 'bg-rose-500' : 'bg-emerald-500'
        }`}>
          {mensaje.type==='error' ? <XCircle size={14}/> : <CheckCircle size={14}/>}
          {mensaje.text}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl flex-shrink-0" style={bgPrimaryLight}>
            <AlertTriangle className="w-4 h-4" style={textPrimary} />
          </div>
          <div>
            <h1 className="text-base font-bold text-gray-900 leading-tight">Gestión de Incidencias</h1>
            <p className="text-xs text-gray-400">Panel de control y seguimiento</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {vistaActiva === 'lista' && (
            <button onClick={exportarCSV}
              className="px-3 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-1.5 font-medium transition-all">
              <Download size={13}/> Exportar CSV
            </button>
          )}
          {vistaActiva === 'estadisticas' && estadisticas && (
            <button onClick={exportarEstadisticasXLSX}
              className="px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100 text-sm flex items-center gap-1.5 font-medium transition-all">
              <FileSpreadsheet size={13}/> Exportar XLSX
            </button>
          )}
        </div>
      </div>

      {/* Tabs — más grandes */}
      <div className="bg-white border-b border-gray-100 flex-shrink-0">
        <nav className="px-6 flex">
          {[
            { id:'lista',        icon:<FileText size={15}/>,  label:'Listado' },
            { id:'nueva',        icon:<Plus size={15}/>,      label:'Nueva' },
            { id:'estadisticas', icon:<BarChart2 size={15}/>, label:'Estadísticas' },
            { id:'reporte',      icon:<Calendar size={15}/>,  label:'Reporte' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setVistaActiva(tab.id)}
              className="py-4 px-5 border-b-2 font-semibold text-sm transition-all duration-150 flex items-center gap-2"
              style={vistaActiva===tab.id
                ? { borderColor:theme.primary, color:theme.primary }
                : { borderColor:'transparent', color:'#9ca3af' }}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Content — ÚNICO scroll aquí ─────────────────────────────────── */}
      <div style={{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column', minHeight:0 }}>

        {/* Vista: Lista */}
        {vistaActiva === 'lista' && (
          <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minHeight:0 }}>
            {/* Filtros */}
            <div className="bg-white border-b border-gray-100 px-6 py-3 flex-shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1 rounded-md" style={bgPrimaryLight}>
                  <Filter size={11} style={textPrimary}/>
                </div>
                <span className="font-semibold text-gray-600 text-sm">Filtros</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                <div className="relative lg:col-span-2">
                  <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input type="text" value={filtros.nombre} placeholder="Buscar por nombre..."
                    onChange={e => setFiltros(p=>({...p,nombre:e.target.value}))} className={searchInputCls}/>
                </div>
                <select value={filtros.estado} onChange={e=>setFiltros(p=>({...p,estado:e.target.value}))} className={inputCls}>
                  <option value="">Todos los estados</option>
                  {ESTADOS.map(e=><option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
                <select value={filtros.prioridad} onChange={e=>setFiltros(p=>({...p,prioridad:e.target.value}))} className={inputCls}>
                  <option value="">Prioridades</option>
                  {PRIORIDADES.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <select value={filtros.tipo} onChange={e=>setFiltros(p=>({...p,tipo:e.target.value}))} className={inputCls}>
                  <option value="">Todos los tipos</option>
                  {TIPOS.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
                <input type="date" value={filtros.fecha_desde}
                  onChange={e=>setFiltros(p=>({...p,fecha_desde:e.target.value}))} className={inputCls}/>
                <input type="date" value={filtros.fecha_hasta}
                  onChange={e=>setFiltros(p=>({...p,fecha_hasta:e.target.value}))} className={inputCls}/>
              </div>
              <div className="flex justify-between items-center gap-2 mt-2">
                {/* Toggle Tablero / Tabla */}
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white">
                  {[
                    { id:'tablero', label:'Tablero', icon:<Activity size={13}/> },
                    { id:'tabla',   label:'Tabla',   icon:<FileText size={13}/> },
                  ].map(v => (
                    <button key={v.id} onClick={()=>setListaVista(v.id)}
                      className="px-3 py-2 text-sm font-semibold flex items-center gap-1.5 transition-all"
                      style={listaVista===v.id
                        ? { backgroundColor:theme.primary, color:'#fff' }
                        : { color:'#6b7280' }}>
                      {v.icon}{v.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => {
                    const f={nombre:'',cliente_id:'',estado:'',prioridad:'',tipo:'',fecha_desde:'',fecha_hasta:''};
                    setFiltros(f); setPage(1); fetchIncidencias(1,f);
                  }} className="px-3 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-sm font-medium">
                    Limpiar
                  </button>
                  <button onClick={()=>{setPage(1);fetchIncidencias(1);}}
                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm font-semibold shadow-sm" style={bgPrimary}>
                    Aplicar
                  </button>
                </div>
              </div>
            </div>

            {/* Tabla — único scroll */}
            <div style={{ flex:1, overflowY:'auto', minHeight:0 }}>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-gray-200"
                    style={{ borderTopColor:theme.primary }}/>
                </div>
              ) : listaVista === 'tablero' ? (
                <div className="p-4">
                  <style>{`
                    @keyframes incCardIn { from { opacity:0; transform: translateY(7px) scale(.98) } to { opacity:1; transform:none } }
                    .inc-card { animation: incCardIn .34s cubic-bezier(.22,1,.36,1) both; transition: transform .16s ease, box-shadow .16s ease; cursor: grab; }
                    .inc-card:hover { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(15,40,60,.10); }
                    .inc-card:active { cursor: grabbing; }
                    .inc-card.dragging { opacity:.35; }
                    .inc-col { transition: background-color .18s ease, box-shadow .18s ease, border-color .18s ease; }
                    @media (prefers-reduced-motion: reduce) { .inc-card { animation: none } .inc-card:hover { transform:none } }
                  `}</style>
                  <div className="flex gap-4 overflow-x-auto pb-2">
                    {KANBAN_COLS.map(col => {
                      const items = incidencias.filter(i => (i.estado || 'abierta') === col.key);
                      const activa = dropCol === col.key;
                      return (
                        <div key={col.key}
                          onDragOver={(e)=>{ e.preventDefault(); if (dropCol !== col.key) setDropCol(col.key); }}
                          onDragLeave={(e)=>{ if (e.currentTarget === e.target) setDropCol(null); }}
                          onDrop={(e)=>{
                            e.preventDefault();
                            const id = Number(e.dataTransfer.getData('text/plain'));
                            const inc = incidencias.find(x=>x.id===id);
                            if (inc) cambiarEstadoInc(inc, col.key);
                            setDropCol(null); setDragId(null);
                          }}
                          className="inc-col flex-shrink-0 w-72 rounded-2xl border p-2"
                          style={{ backgroundColor: activa ? `${col.color}12` : '#f8fafc',
                                   borderColor: activa ? col.color : '#eef2f6',
                                   boxShadow: activa ? `inset 0 0 0 2px ${col.color}66` : 'none' }}>
                          {/* Header de columna */}
                          <div className="flex items-center justify-between px-2 py-2">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }}/>
                              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{col.label}</span>
                            </div>
                            <span className="text-[11px] font-bold text-gray-400 bg-white rounded-full px-2 py-0.5 border border-gray-100">{items.length}</span>
                          </div>
                          {/* Tarjetas */}
                          <div className="space-y-2 min-h-[110px] px-1 pb-1">
                            {items.length === 0 ? (
                              <div className="text-center text-[11px] text-gray-300 py-9 border-2 border-dashed border-gray-200/70 rounded-xl">
                                Arrastra aquí
                              </div>
                            ) : items.map((inc, idx) => (
                              <div key={inc.id} draggable
                                onDragStart={(e)=>{ setDragId(inc.id); e.dataTransfer.setData('text/plain', String(inc.id)); e.dataTransfer.effectAllowed='move'; }}
                                onDragEnd={()=>{ setDragId(null); setDropCol(null); }}
                                onClick={()=>setModalDetalle(inc)}
                                className={`inc-card bg-white rounded-xl border border-gray-100 shadow-sm p-3 ${dragId===inc.id?'dragging':''}`}
                                style={{ animationDelay:`${Math.min(idx,12)*28}ms`, borderLeft:`3px solid ${PRIO_COLORS[inc.prioridad]||'#cbd5e1'}` }}>
                                <div className="text-xs font-semibold text-gray-800 leading-snug line-clamp-2">{inc.nombre}</div>
                                {inc.tipo_incidencia && (
                                  <div className="mt-1.5">
                                    <span className="inline-block text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                                      style={{ backgroundColor:`${theme.primary}14`, color:theme.primary }}>{inc.tipo_incidencia}</span>
                                  </div>
                                )}
                                <div className="flex items-center justify-between mt-2.5">
                                  <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold ${getPrioridadBadge(inc.prioridad)}`}>
                                    {inc.prioridad ? inc.prioridad.charAt(0).toUpperCase()+inc.prioridad.slice(1) : '—'}
                                  </span>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {inc.adjuntos_count > 0 && (
                                      <span className="flex items-center gap-0.5 text-gray-400" style={{ fontSize:'10px' }}
                                        title={`${inc.adjuntos_count} imagen(es) adjunta(s)`}>
                                        <Paperclip size={9}/>{inc.adjuntos_count}
                                      </span>
                                    )}
                                    <span className="text-[10px] font-mono text-gray-300">#{inc.id}</span>
                                  </div>
                                </div>
                                {/* Sede dentro de la red: badge + matriz de la que cuelga */}
                                <div className="flex items-center gap-1 mt-2" style={{ fontSize:'9px' }}>
                                  <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wide flex-shrink-0"
                                    style={esSede(inc) ? SEDE_BADGE : MATRIZ_BADGE}>
                                    {esSede(inc) ? 'Sede' : 'Matriz'}
                                  </span>
                                  {esSede(inc) && (
                                    <span className="text-gray-400 truncate" title={inc.matriz_nombre || ''}>
                                      de {nombreMatriz(inc)}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50 text-[10px] text-gray-400">
                                  <div className="flex items-center gap-1 min-w-0">
                                    <Building2 size={10} className="flex-shrink-0"/>
                                    <span className="truncate">{inc.cliente_nombre}</span>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <Calendar size={10}/>
                                    <span>{inc.fecha ? new Date(inc.fecha).toLocaleDateString('es-ES',{day:'2-digit',month:'short'}) : '—'}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : incidencias.length > 0 ? (
                <div className="p-4">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            {['ID','Nombre','Cliente','Sede','Fecha','Tipo','Prioridad','Estado','Horas','Acciones'].map(h=>(
                              <th key={h} className={`px-4 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest ${h==='Acciones'?'text-right':'text-left'}`}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {incidencias.map(inc => {
                            const { dot, label:estadoLabel } = getEstadoBadge(inc.estado);
                            return (
                              <tr key={inc.id} className="hover:bg-gray-50/70 transition-colors group">
                                <td className="px-4 py-3 text-[10px] font-mono text-gray-300">#{inc.id}</td>
                                <td className="px-4 py-3"><div className="text-xs font-semibold text-gray-800 max-w-xs truncate">{inc.nombre}</div></td>
                                <td className="px-4 py-3">
                                  <div className="text-xs text-gray-700 font-medium">{inc.cliente_nombre}</div>
                                  {inc.cliente_alias && <div className="text-[10px] text-gray-400">{inc.cliente_alias}</div>}
                                </td>
                                {/* Sede: el centro concreto ya se ve en "Cliente"; aquí decimos
                                    QUÉ ES ese centro dentro de la red y de quién cuelga. */}
                                <td className="px-4 py-3">
                                  {esSede(inc) ? (
                                    <>
                                      <span className="inline-flex px-2 py-0.5 rounded-md font-semibold"
                                        style={{ ...SEDE_BADGE, fontSize: '10px' }}>Sede</span>
                                      <div className="text-gray-400 truncate"
                                        style={{ fontSize: '10px', maxWidth: '150px' }}
                                        title={inc.matriz_nombre || ''}>
                                        de {nombreMatriz(inc)}
                                      </div>
                                    </>
                                  ) : (
                                    <span className="inline-flex px-2 py-0.5 rounded-md font-semibold"
                                      style={{ ...MATRIZ_BADGE, fontSize: '10px' }}>Matriz</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                                  {inc.fecha ? new Date(inc.fecha).toLocaleDateString('es-ES') : '—'}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{inc.tipo_incidencia||'—'}</td>
                                <td className="px-4 py-3">
                                  <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold ${getPrioridadBadge(inc.prioridad)}`}>
                                    {inc.prioridad ? inc.prioridad.charAt(0).toUpperCase()+inc.prioridad.slice(1) : '—'}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1.5">
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`}/>
                                    <span className="text-xs text-gray-600 font-medium">{estadoLabel}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap font-medium">
                                  {inc.carga_trabajo!=null ? `${parseFloat(inc.carga_trabajo).toFixed(2)}h` : '—'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={()=>setModalDetalle(inc)} className="p-1.5 rounded-lg hover:bg-sky-50 text-gray-400 hover:text-sky-600 transition-all"><Eye size={13}/></button>
                                    <button onClick={()=>abrirEditar(inc)} className="p-1.5 rounded-lg hover:bg-amber-50 text-gray-400 hover:text-amber-500 transition-all"><Edit2 size={13}/></button>
                                    <button onClick={()=>setModalEliminar(inc)} className="p-1.5 rounded-lg hover:bg-rose-50 text-gray-400 hover:text-rose-500 transition-all"><Trash2 size={13}/></button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
                        <span className="text-xs text-gray-400 font-medium">Página {page} de {totalPages}</span>
                        <div className="flex gap-1.5">
                          <button disabled={page<=1} onClick={()=>{const p=page-1;setPage(p);fetchIncidencias(p);}}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-white text-gray-600">Anterior</button>
                          <button disabled={page>=totalPages} onClick={()=>{const p=page+1;setPage(p);fetchIncidencias(p);}}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-white text-gray-600">Siguiente</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-gray-300"/>
                  </div>
                  <div className="text-center">
                    <h3 className="text-sm font-bold text-gray-600 mb-1">No hay incidencias</h3>
                    <p className="text-gray-400 text-xs">No se encontraron resultados con los filtros actuales.</p>
                  </div>
                  <button onClick={()=>setVistaActiva('nueva')}
                    className="mt-1 px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm font-semibold flex items-center gap-2 shadow-sm" style={bgPrimary}>
                    <Plus size={13}/> Crear primera incidencia
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vista: Nueva */}
        {vistaActiva === 'nueva' && (
          <div style={{ flex:1, overflowY:'auto', minHeight:0, padding:'20px' }}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                <div className="p-2 rounded-lg" style={bgPrimaryLight}>
                  <Plus size={15} style={textPrimary}/>
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-800">Nueva Incidencia</h2>
                  <p className="text-xs text-gray-400">Completa los campos para registrar la incidencia</p>
                </div>
              </div>
              <IncidenciaForm
                data={formData}
                onChange={(k,v) => setFormData(p=>({...p,[k]:v}))}
                archivos={formArchivos}
                onArchivosAdd={anadirArchivos(setFormArchivos)}
                onArchivoRemove={quitarArchivo(setFormArchivos)}
                clienteSearch={clienteSearch}
                onClienteSearch={handleClienteSearch}
                clienteResults={clienteResults}
                onClienteSelect={c => {
                  setFormData(p=>({...p,cliente_id:c.id,cliente_nombre:c.razon_social}));
                  setClienteSearch(''); setClienteResults([]);
                }}
                onClienteRemove={() => setFormData(p=>({...p,cliente_id:'',cliente_nombre:''}))}
              />
              <div className="flex justify-end gap-2.5 mt-5 pt-4 border-t border-gray-100">
                <button onClick={()=>{setFormData(emptyForm);setClienteSearch('');setVistaActiva('lista');}}
                  className="px-5 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-sm font-medium">
                  Cancelar
                </button>
                <button onClick={handleCrear}
                  className="px-5 py-2 text-white rounded-lg hover:opacity-90 text-sm font-semibold flex items-center gap-2 shadow-sm" style={bgPrimary}>
                  <CheckCircle size={13}/> Crear Incidencia
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vista: Reporte mensual */}
        {vistaActiva === 'reporte' && (
          <div style={{ flex:1, overflowY:'auto', minHeight:0, padding:'16px' }}>
            <div className="max-w-5xl mx-auto space-y-4">

              {/* Filtros del reporte */}
              <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-md" style={bgPrimaryLight}><Filter size={13} style={textPrimary}/></div>
                  <span className="font-semibold text-gray-700 text-sm">Filtros del reporte</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>Desde</label>
                    <input type="date" value={repFiltros.desde}
                      onChange={e => setRepFiltros(p => ({ ...p, desde:e.target.value }))} className={inputCls}/>
                  </div>
                  <div>
                    <label className={labelCls}>Hasta</label>
                    <input type="date" value={repFiltros.hasta}
                      onChange={e => setRepFiltros(p => ({ ...p, hasta:e.target.value }))} className={inputCls}/>
                  </div>
                  <div className="relative">
                    <label className={labelCls}>Añadir clínica (vacío = todas)</label>
                    <div className="relative">
                      <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                      <input type="text" value={repClienteSearch} placeholder="Buscar clínica..."
                        onChange={e => buscarClientesRep(e.target.value)} className={searchInputCls}/>
                    </div>
                    {repClienteResults.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                        {repClienteResults.map(c => (
                          <button key={c.id} onClick={() => addClinicaRep(c)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
                            <Building2 size={13} className="text-gray-400"/>
                            <span className="truncate">{c.razon_social || c.alias || `#${c.id}`}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Chips de clínicas seleccionadas */}
                {repClinicas.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {repClinicas.map(c => (
                      <span key={c.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {c.razon_social}
                        <button onClick={() => removeClinicaRep(c.id)} className="hover:text-emerald-900"><X size={11}/></button>
                      </span>
                    ))}
                    <button onClick={() => setRepClinicas([])}
                      className="text-xs text-gray-400 hover:text-gray-600 px-1">Limpiar</button>
                  </div>
                )}

                <div className="flex justify-end mt-3">
                  <button onClick={fetchReporteDatos} disabled={repLoading}
                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm font-semibold flex items-center gap-2 shadow-sm disabled:opacity-50" style={bgPrimary}>
                    <BarChart2 size={13}/> {repLoading ? 'Calculando...' : 'Actualizar métricas'}
                  </button>
                </div>
              </div>

              {/* Métricas (preview) */}
              {repDatos && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label:'Creadas',    value:repDatos.resumen.creadas,    color:'#0ea5e9' },
                      { label:'Resueltas',  value:repDatos.resumen.resueltas,  color:'#10b981' },
                      { label:'Pendientes', value:repDatos.resumen.pendientes, color:'#f59e0b' },
                      { label:'Horas',      value:repDatos.resumen.horas,      color:'#6366f1' },
                    ].map(k => (
                      <div key={k.label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <div className="text-2xl font-bold" style={{ color:k.color }}>
                          {Number(k.value).toLocaleString('es-ES', { maximumFractionDigits:1 })}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">{k.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Tabla por clínica */}
                  <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
                      <Building2 size={14} className="text-gray-400"/>
                      <span className="font-semibold text-gray-700 text-sm">Detalle por clínica</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-gray-400 border-b border-gray-100">
                            <th className="text-left font-medium px-4 py-2">Clínica</th>
                            <th className="text-right font-medium px-4 py-2">Creadas</th>
                            <th className="text-right font-medium px-4 py-2">Resueltas</th>
                            <th className="text-right font-medium px-4 py-2">Pendientes</th>
                            <th className="text-right font-medium px-4 py-2">Horas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {repDatos.por_clinica.length === 0 ? (
                            <tr><td colSpan={5} className="text-center text-gray-400 py-6">Sin incidencias en el periodo seleccionado</td></tr>
                          ) : repDatos.por_clinica.map(c => (
                            <tr key={c.cliente_id} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="px-4 py-2 text-gray-700">{c.razon_social}</td>
                              <td className="px-4 py-2 text-right text-gray-700">{c.creadas}</td>
                              <td className="px-4 py-2 text-right text-emerald-600 font-medium">{c.resueltas}</td>
                              <td className="px-4 py-2 text-right text-amber-600">{c.pendientes}</td>
                              <td className="px-4 py-2 text-right text-gray-500">{Number(c.horas).toLocaleString('es-ES',{maximumFractionDigits:1})}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {/* Acciones: descargar / enviar */}
              <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-md" style={bgPrimaryLight}><Download size={13} style={textPrimary}/></div>
                  <span className="font-semibold text-gray-700 text-sm">Descargar o enviar</span>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <button onClick={() => descargarReporte('xlsx')}
                    className="px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100 text-sm font-medium flex items-center gap-1.5">
                    <FileSpreadsheet size={14}/> Excel
                  </button>
                  <button onClick={() => descargarReporte('pdf')}
                    className="px-4 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg hover:bg-rose-100 text-sm font-medium flex items-center gap-1.5">
                    <FileText size={14}/> PDF
                  </button>

                  <div className="h-8 w-px bg-gray-200 mx-1 hidden md:block"/>

                  <div>
                    <label className={labelCls}>Formato email</label>
                    <select value={repFormato} onChange={e => setRepFormato(e.target.value)} className={inputCls}>
                      <option value="ambos">Excel + PDF</option>
                      <option value="xlsx">Solo Excel</option>
                      <option value="pdf">Solo PDF</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className={labelCls}>Email destino (vacío = soporte@centralsalud.es)</label>
                    <input type="email" value={repEmail} placeholder="soporte@centralsalud.es"
                      onChange={e => setRepEmail(e.target.value)} className={inputCls}/>
                  </div>
                  <button onClick={enviarReporteEmail} disabled={repEnviando}
                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm font-semibold flex items-center gap-2 shadow-sm disabled:opacity-50" style={bgPrimary}>
                    <CheckCircle size={13}/> {repEnviando ? 'Enviando...' : 'Enviar por email'}
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Vista: Estadísticas */}
        {vistaActiva === 'estadisticas' && (
          <div style={{ flex:1, overflowY:'auto', minHeight:0, padding:'16px' }}>
            {!estadisticas ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-gray-200"
                  style={{ borderTopColor:theme.primary }}/>
              </div>
            ) : (
              <div className="space-y-4">

                {/* KPIs */}
                <div className="grid grid-cols-4 xl:grid-cols-8 gap-3">
                  {[
                    { label:'Total',       value:estadisticas.kpis?.total_incidencias??0,
                      sub:`${parseFloat(estadisticas.kpis?.total_horas??0).toFixed(1)}h totales`,
                      icon:<AlertTriangle size={13}/>, colorText:'text-violet-600', colorBg:'bg-violet-50', border:'border-violet-100' },
                    { label:'Abiertas',    value:estadisticas.kpis?.abiertas??0,
                      sub:'estado abierta',
                      icon:<Clock size={13}/>, colorText:'text-sky-600', colorBg:'bg-sky-50', border:'border-sky-100' },
                    { label:'En proceso',  value:estadisticas.kpis?.en_proceso??0,
                      sub:'en trabajo',
                      icon:<Activity size={13}/>, colorText:'text-amber-600', colorBg:'bg-amber-50', border:'border-amber-100' },
                    { label:'Resueltas',   value:estadisticas.kpis?.resueltas??0,
                      sub:'completadas',
                      icon:<CheckCircle size={13}/>, colorText:'text-emerald-600', colorBg:'bg-emerald-50', border:'border-emerald-100' },
                    { label:'Cerradas',    value:estadisticas.kpis?.cerradas??0,
                      sub:'archivadas',
                      icon:<XCircle size={13}/>, colorText:'text-gray-500', colorBg:'bg-gray-100', border:'border-gray-200' },
                    { label:'Este mes',    value:estadisticas.kpis?.este_mes??0,
                      sub:`${parseFloat(estadisticas.kpis?.horas_mes??0).toFixed(1)}h este mes`,
                      icon:<Calendar size={13}/>, colorText:'text-indigo-600', colorBg:'bg-indigo-50', border:'border-indigo-100' },
                    { label:'Críticas',    value:estadisticas.kpis?.criticas??0,
                      sub:'prioridad crítica',
                      icon:<Zap size={13}/>, colorText:'text-rose-600', colorBg:'bg-rose-50', border:'border-rose-100' },
                    { label:'Días medio',  value:parseFloat(estadisticas.kpis?.avg_resolucion_dias??0).toFixed(1),
                      sub:'resolución media',
                      icon:<TrendingUp size={13}/>, colorText:'text-teal-600', colorBg:'bg-teal-50', border:'border-teal-100' },
                  ].map(k=><StatCard key={k.label} {...k}/>)}
                </div>

                {/* Evolución mensual */}
                <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" style={{ height:220 }}>
                  <div className="flex items-center justify-between mb-2 flex-shrink-0">
                    <h3 className="font-semibold text-gray-700 flex items-center gap-2 text-xs">
                      <div className="p-1 rounded-md" style={bgPrimaryLight}><Activity size={11} style={textPrimary}/></div>
                      Evolución mensual — Incidencias y horas
                    </h3>
                    <ChartToggle value={ct.evolucion} onChange={v=>setChartType('evolucion',v)}
                      options={[{value:'composed',label:'Área'},{value:'bar',label:'Barras'},{value:'line',label:'Línea'}]}/>
                  </div>
                  <div style={{ height:162 }}>{renderEvolucion()}</div>
                </div>

                {/* Top clientes */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" style={{ height:260 }}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-700 flex items-center gap-2 text-xs">
                        <div className="p-1 rounded-md" style={bgPrimaryLight}><Users size={11} style={textPrimary}/></div>
                        Top clientes — Nº incidencias
                      </h3>
                      <ChartToggle value={ct.topInc} onChange={v=>setChartType('topInc',v)}
                        options={[{value:'bar',label:'Barras'},{value:'line',label:'Línea'}]}/>
                    </div>
                    <div style={{ height:208 }}>
                      {renderHBarOrLine(
                        estadisticas.top_clientes?.slice(0,10).map(c=>({
                          name: c.razon_social.length>16?c.razon_social.slice(0,16)+'…':c.razon_social,
                          total:c.total, pendientes:c.pendientes,
                        })),
                        'total','name',theme.primary,'topInc'
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" style={{ height:260 }}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-700 flex items-center gap-2 text-xs">
                        <div className="p-1 rounded-md" style={bgPrimaryLight}><Clock size={11} style={textPrimary}/></div>
                        Top clientes — Horas dedicadas
                      </h3>
                      <ChartToggle value={ct.topHoras} onChange={v=>setChartType('topHoras',v)}
                        options={[{value:'bar',label:'Barras'},{value:'line',label:'Línea'}]}/>
                    </div>
                    <div style={{ height:208 }}>
                      {renderHBarOrLine(
                        estadisticas.top_clientes_horas?.slice(0,10).map(c=>({
                          name: c.razon_social.length>16?c.razon_social.slice(0,16)+'…':c.razon_social,
                          horas: parseFloat(parseFloat(c.horas||0).toFixed(1)),
                        })),
                        'horas','name','#f59e0b','topHoras'
                      )}
                    </div>
                  </div>
                </div>

                {/* Distribuciones */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" style={{ height:240 }}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-700 flex items-center gap-2 text-xs">
                        <div className="p-1 rounded-md" style={bgPrimaryLight}><BarChart2 size={11} style={textPrimary}/></div>
                        Por tipo
                      </h3>
                      <ChartToggle value={ct.tipo} onChange={v=>setChartType('tipo',v)}
                        options={[{value:'pie',label:'Circular'},{value:'bar',label:'Barras'}]}/>
                    </div>
                    <div style={{ height:188 }}>
                      {renderPieOrBar(
                        estadisticas.distribucion_tipo?.slice(0,8).map(t=>({...t,tipo_incidencia:t.tipo_incidencia||'Sin tipo'})),
                        'total','tipo_incidencia','tipo',(_,i)=>PIE_COLORS[i%PIE_COLORS.length]
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" style={{ height:240 }}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-700 flex items-center gap-2 text-xs">
                        <div className="p-1 rounded-md" style={bgPrimaryLight}><Zap size={11} style={textPrimary}/></div>
                        Por prioridad
                      </h3>
                      <ChartToggle value={ct.prioridad} onChange={v=>setChartType('prioridad',v)}
                        options={[{value:'bar',label:'Barras'},{value:'pie',label:'Donut'}]}/>
                    </div>
                    <div style={{ height:188 }}>
                      {renderPieOrBar(
                        estadisticas.distribucion_prioridad?.map(p=>({
                          ...p,
                          prioridad: p.prioridad?p.prioridad.charAt(0).toUpperCase()+p.prioridad.slice(1):'N/A',
                          prioridadRaw: p.prioridad,
                        })),
                        'total','prioridad','prioridad',
                        d=>PRIO_COLORS[d.prioridadRaw]||'#9ca3af'
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" style={{ height:240 }}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-700 flex items-center gap-2 text-xs">
                        <div className="p-1 rounded-md" style={bgPrimaryLight}><Activity size={11} style={textPrimary}/></div>
                        Por estado
                      </h3>
                      <ChartToggle value={ct.estado} onChange={v=>setChartType('estado',v)}
                        options={[{value:'pie',label:'Donut'},{value:'bar',label:'Barras'}]}/>
                    </div>
                    <div style={{ height:188 }}>
                      {renderPieOrBar(
                        estadisticas.distribucion_estado?.map(e=>({
                          ...e, estadoLabel:getEstadoBadge(e.estado).label, estadoRaw:e.estado,
                        })),
                        'total','estadoLabel','estado',
                        d=>ESTADO_COLORS[d.estadoRaw]||'#9ca3af'
                      )}
                    </div>
                  </div>
                </div>

                {/* Horas por tipo */}
                <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm" style={{ height:220 }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-700 flex items-center gap-2 text-xs">
                      <div className="p-1 rounded-md" style={bgPrimaryLight}><Clock size={11} style={textPrimary}/></div>
                      Horas acumuladas por tipo
                    </h3>
                    <ChartToggle value={ct.horasTipo} onChange={v=>setChartType('horasTipo',v)}
                      options={[{value:'bar',label:'Barras'},{value:'line',label:'Línea'}]}/>
                  </div>
                  <div style={{ height:162 }}>
                    {renderHBarOrLine(
                      estadisticas.distribucion_tipo?.slice(0,10).map(t=>({
                        name: (t.tipo_incidencia||'Sin tipo').length>22?(t.tipo_incidencia||'Sin tipo').slice(0,22)+'…':(t.tipo_incidencia||'Sin tipo'),
                        horas: parseFloat(parseFloat(t.horas||0).toFixed(1)),
                      })),
                      'horas','name','#6366f1','horasTipo'
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modal: Detalle ──────────────────────────────────────────────── */}
      {modalDetalle && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden">
            <div className="text-white px-6 py-4 flex items-center justify-between" style={bgPrimary}>
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-white/20 rounded-lg"><Eye size={15}/></div>
                <div>
                  <h2 className="text-sm font-bold">Detalle de Incidencia</h2>
                  <p className="opacity-75" style={{ fontSize:'10px' }}>
                    #{D.id} · {D.cliente_nombre}{esSede(D) ? ` · sede de ${nombreMatriz(D)}` : ' · matriz'}
                  </p>
                </div>
              </div>
              <button onClick={()=>setModalDetalle(null)} className="p-1.5 rounded-lg hover:bg-white/20"><X size={15}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Mientras llega GET /:id se pinta lo que ya tenemos del listado y
                  se avisa con una barrita; nada de pantalla en blanco. */}
              {detalleLoading && !detalle && (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-200"
                    style={{ borderTopColor: theme.primary }}/>
                  Cargando ficha completa...
                </div>
              )}

              <div className="flex items-start justify-between gap-3">
                <h3 className="text-base font-bold text-gray-900 flex-1">{D.nombre}</h3>
                <div className="flex gap-1.5 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${getPrioridadBadge(D.prioridad)}`}
                    style={{ fontSize:'10px' }}>{D.prioridad || '—'}</span>
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-md">
                    <span className={`w-1.5 h-1.5 rounded-full ${getEstadoBadge(D.estado).dot}`}
                      style={{ backgroundColor: ESTADO_COLORS[D.estado] || '#9ca3af' }}/>
                    <span className="text-gray-600 font-semibold" style={{ fontSize:'10px' }}>
                      {getEstadoBadge(D.estado).label}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Sede / posición en la red ─────────────────────────────── */}
              <div className="rounded-lg p-3 border border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2 mb-1.5">
                  <Network size={12} className="text-gray-400 flex-shrink-0"/>
                  <p className="font-bold text-gray-400 uppercase tracking-wider" style={{ fontSize:'9px' }}>Sede</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-800 text-xs">{D.cliente_nombre || '—'}</span>
                  <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wide"
                    style={{ ...(esSede(D) ? SEDE_BADGE : MATRIZ_BADGE), fontSize:'9px' }}>
                    {esSede(D) ? 'Sede' : 'Matriz'}
                  </span>
                </div>
                <p className="text-gray-500 mt-1" style={{ fontSize:'11px' }}>
                  {esSede(D)
                    ? <>Pertenece a la clínica matriz <span className="font-semibold text-gray-700">{D.matriz_nombre || nombreMatriz(D)}</span></>
                    : 'Es la clínica matriz de la red'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {[['Empresa / centro', D.cliente_nombre || '—'],
                  ['Tipo', D.tipo_incidencia || '—'],
                  ['Fecha de alta', D.fecha ? new Date(D.fecha).toLocaleDateString('es-ES') : '—'],
                  ['Fecha resolución', D.fecha_resolucion ? new Date(D.fecha_resolucion).toLocaleDateString('es-ES') : '—'],
                  ['Carga de trabajo', D.carga_trabajo != null ? `${parseFloat(D.carga_trabajo).toFixed(2)}h` : '—'],
                  ['Responsable', D.usuario_nombre || '—'],
                ].map(([label,val])=>(
                  <div key={label} className="bg-gray-50 rounded-lg p-3">
                    <p className="font-bold text-gray-400 uppercase tracking-wider mb-0.5" style={{ fontSize:'9px' }}>{label}</p>
                    <p className="font-semibold text-gray-800 text-xs break-words">{val}</p>
                  </div>
                ))}
              </div>

              {/* Contacto — solo lo trae GET /:id, por eso puede tardar un instante */}
              {(D.cliente_telefono || D.cliente_email) && (
                <div className="flex flex-wrap gap-3">
                  {D.cliente_telefono && (
                    <a href={`tel:${D.cliente_telefono}`}
                      className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <Phone size={12} className="text-gray-400"/> {D.cliente_telefono}
                    </a>
                  )}
                  {D.cliente_email && (
                    <a href={`mailto:${D.cliente_email}`}
                      className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 break-all">
                      <Mail size={12} className="text-gray-400"/> {D.cliente_email}
                    </a>
                  )}
                </div>
              )}

              {D.descripcion && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="font-bold text-gray-400 uppercase tracking-wider mb-1.5" style={{ fontSize:'9px' }}>Descripción</p>
                  <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{D.descripcion}</p>
                </div>
              )}

              {D.resolucion && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-bold text-green-700 uppercase tracking-wider mb-1.5" style={{ fontSize:'9px' }}>Notas de resolución</p>
                  <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{D.resolucion}</p>
                </div>
              )}

              {/* ── Galería de imágenes adjuntas ──────────────────────────── */}
              {detalleImgs.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ImageIcon size={12} className="text-gray-400"/>
                    <p className="font-bold text-gray-400 uppercase tracking-wider" style={{ fontSize:'9px' }}>
                      Imágenes adjuntas ({detalleImgs.length})
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {detalleImgs.map((img, idx) => (
                      <button key={img.id} type="button" onClick={() => openLightbox(idx)}
                        title={img.nombre_original}
                        className="rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors"
                        style={{ lineHeight: 0 }}>
                        <img src={archivoSrc(img.url)} alt={img.nombre_original}
                          style={{ width:'104px', height:'104px', objectFit:'cover', display:'block' }}/>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-2 bg-gray-50/50">
              <button onClick={()=>{abrirEditar(D);setModalDetalle(null);}}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm font-semibold flex items-center gap-1.5 shadow-sm" style={bgPrimary}>
                <Edit2 size={12}/> Editar
              </button>
              <button onClick={()=>setModalDetalle(null)}
                className="px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-100 text-sm font-medium">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Lightbox de adjuntos ────────────────────────────────────────────
          Misma lógica que BlogPost.js (índice, flechas, Escape, stopPropagation);
          el markup se reimplementa aquí porque allí venía de Blog.css.
          Colores y z-index inline: no dependen de utilidades Tailwind. */}
      {lightboxOpen && detalleImgs.length > 0 && (
        <div onClick={closeLightbox}
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ backgroundColor:'rgba(0,0,0,.92)', zIndex:60 }}>
          {/* Parar la propagación: si no, clicar la imagen cierra el lightbox */}
          <div onClick={e => e.stopPropagation()}
            className="relative flex flex-col items-center"
            style={{ maxWidth:'92vw', maxHeight:'92vh' }}>

            <button onClick={closeLightbox} title="Cerrar (Esc)"
              className="absolute text-white rounded-full flex items-center justify-center hover:opacity-100"
              style={{ top:'-6px', right:'-6px', width:'34px', height:'34px',
                       backgroundColor:'rgba(255,255,255,.15)', opacity:.85 }}>
              <X size={20}/>
            </button>

            {detalleImgs.length > 1 && (
              <>
                <button onClick={() => navigateLightbox(-1)} title="Anterior (←)"
                  className="absolute text-white rounded-full flex items-center justify-center"
                  style={{ left:'-52px', top:'50%', marginTop:'-22px', width:'44px', height:'44px',
                           backgroundColor:'rgba(255,255,255,.15)' }}>
                  <ChevronLeft size={30}/>
                </button>
                <button onClick={() => navigateLightbox(1)} title="Siguiente (→)"
                  className="absolute text-white rounded-full flex items-center justify-center"
                  style={{ right:'-52px', top:'50%', marginTop:'-22px', width:'44px', height:'44px',
                           backgroundColor:'rgba(255,255,255,.15)' }}>
                  <ChevronRight size={30}/>
                </button>
              </>
            )}

            <img
              src={archivoSrc(detalleImgs[lightboxIndex]?.url)}
              alt={detalleImgs[lightboxIndex]?.nombre_original || ''}
              style={{ maxWidth:'92vw', maxHeight:'80vh', objectFit:'contain', display:'block',
                       borderRadius:'8px', backgroundColor:'#111827' }}/>

            <div className="text-white text-center mt-3 flex items-center gap-3">
              <span className="text-xs truncate" style={{ maxWidth:'60vw', opacity:.85 }}>
                {detalleImgs[lightboxIndex]?.nombre_original}
              </span>
              <span className="text-xs font-semibold" style={{ opacity:.6 }}>
                {lightboxIndex + 1} / {detalleImgs.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Editar ───────────────────────────────────────────────── */}
      {modalEditar && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="text-white px-6 py-4 flex items-center justify-between" style={bgPrimary}>
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-white/20 rounded-lg"><Edit2 size={15}/></div>
                <div>
                  <h2 className="text-sm font-bold">Editar Incidencia</h2>
                  <p className="text-[10px] opacity-75">#{modalEditar.id}</p>
                </div>
              </div>
              <button onClick={()=>setModalEditar(null)} className="p-1.5 rounded-lg hover:bg-white/20"><X size={15}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <IncidenciaForm
                data={editForm}
                onChange={(k,v)=>setEditForm(p=>({...p,[k]:v}))}
                archivos={editArchivos}
                onArchivosAdd={anadirArchivos(setEditArchivos)}
                onArchivoRemove={quitarArchivo(setEditArchivos)}
                adjuntos={editAdjuntos}
                onAdjuntoRemove={quitarAdjuntoServidor}
                clienteSearch={editClienteSearch}
                onClienteSearch={handleEditClienteSearch}
                clienteResults={editClienteResults}
                onClienteSelect={c=>{
                  setEditForm(p=>({...p,cliente_id:c.id,cliente_nombre:c.razon_social}));
                  setEditClienteSearch(''); setEditClienteResults([]);
                }}
                onClienteRemove={()=>setEditForm(p=>({...p,cliente_id:'',cliente_nombre:''}))}
              />
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-2 bg-gray-50/50">
              <button onClick={()=>setModalEditar(null)}
                className="px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-100 text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleEditar}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm font-semibold flex items-center gap-2 shadow-sm" style={bgPrimary}>
                <CheckCircle size={13}/> Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Eliminar ─────────────────────────────────────────────── */}
      {modalEliminar && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-rose-500 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-white/20 rounded-lg"><Trash2 size={15}/></div>
                <div>
                  <h2 className="text-sm font-bold">Eliminar Incidencia</h2>
                  <p className="text-[10px] opacity-75">Esta acción no se puede deshacer</p>
                </div>
              </div>
              <button onClick={()=>setModalEliminar(null)} className="p-1.5 rounded-lg hover:bg-white/20"><X size={15}/></button>
            </div>
            <div className="p-6">
              <div className="space-y-2 mb-6">
                {[['Nombre',modalEliminar.nombre],['Cliente',modalEliminar.cliente_nombre],
                  ['Fecha',modalEliminar.fecha?new Date(modalEliminar.fecha).toLocaleDateString('es-ES'):'—']].map(([k,v])=>(
                  <div key={k} className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-400 w-16">{k}</span>
                    <span className="font-medium text-gray-700">{v}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={()=>setModalEliminar(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-sm font-medium">
                  Cancelar
                </button>
                <button onClick={handleEliminar}
                  className="flex-1 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                  <Trash2 size={13}/> Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}